import { FC, SyntheticEvent, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import { Entity } from 'domain/types/Entity';
import { Facility } from 'domain/entities/Facility';
import { useCaseSearchFacility } from 'application/stations/useCases/useCaseSearchFacility';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import DEFAULT_DEBOUNCE_TIME from 'infrastructure/utils/defaultDebounceTime';
import { Autocomplete } from 'targets/web/components';

export const FacilityMultiAutocomplete: FC<{
  initialValue?: Entity<Facility>[];
  onChange: (facilities: Entity<Facility>[]) => void;
}> = ({ initialValue, onChange }) => {
  const t = useTranslationPrefix('components.filters_container');

  const [facilityPhrase, setFacilityPhrase] = useState('');
  const { items, isLoading } = useCaseSearchFacility({ phrase: facilityPhrase });

  const handleInputChange = useMemo(
    () =>
      debounce((event: SyntheticEvent, newInputValue: string) => {
        if (event?.type !== 'change' && newInputValue) {
          return;
        }

        setFacilityPhrase(newInputValue);
      }, DEFAULT_DEBOUNCE_TIME),
    [],
  );

  return (
    <Autocomplete<Entity<Facility>, true, false, false>
      label={t('facility_label')}
      loading={isLoading}
      options={items}
      getOptionLabel={(option) => option.name}
      defaultValue={initialValue}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x}
      onInputChange={handleInputChange}
      data-testname="facilityFilter"
      onChange={(_, value) => {
        onChange(value);
      }}
      placeholder={t('facility_placeholder')}
      multiple
    />
  );
};
